import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { ConfirmDialog } from '@/components/patterns/ConfirmDialog';
import { toast } from '@/lib/notify';
import { useUiStore } from '@/stores/uiStore';

import { useChangeQuestionnaireStatus } from '../api/useChangeQuestionnaireStatus';
import { useDeleteQuestionnaire } from '../api/useDeleteQuestionnaire';
import { useQuestionnaire } from '../api/useQuestionnaire';
import { useSaveQuestionnaire } from '../api/useSaveQuestionnaire';
import {
  type BuilderField,
  type BuilderModel,
  type BuilderSection,
  emptyModel,
  fromDetail,
  mergeServerIds,
  newField,
  newSection,
  toSaveInput,
} from '../builderModel';
import {
  QuestionnaireBuilderHeader,
  type SaveState,
} from '../components/QuestionnaireBuilderHeader';
import { QuestionnairesConfigPanel } from '../components/QuestionnairesConfigPanel';
import { QuestionnairesMobilePreview } from '../components/QuestionnairesMobilePreview';
import { QuestionnairesSidebar } from '../components/QuestionnairesSidebar';

/**
 * How long after the last edit the autosave fires.
 *
 * 1200ms: long enough that typing a section title is one request rather than twenty, short enough
 * that nobody navigates away inside the window. The beforeunload guard below covers the rest.
 */
const AUTOSAVE_DELAY_MS = 1200;

/** How long "Saved" stays on screen before the indicator goes quiet again. */
const SAVED_BADGE_MS = 2000;

export function QuestionnairesBuilderScreen() {
  const { questionnaireId } = useParams<{ questionnaireId: string }>();
  const navigate = useNavigate();
  const setSidebarCollapsed = useUiStore((s) => s.setSidebarCollapsed);

  const { data, isLoading } = useQuestionnaire(questionnaireId);
  const save = useSaveQuestionnaire(questionnaireId);
  const changeStatus = useChangeQuestionnaireStatus();
  const deleteQuestionnaire = useDeleteQuestionnaire();

  const [model, setModel] = useState<BuilderModel>(emptyModel);
  const [activeSectionKey, setActiveSectionKey] = useState('');
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  // Collapse the app rail like the workflow builder; restore on leave.
  useEffect(() => {
    const previous = useUiStore.getState().sidebarCollapsed;
    setSidebarCollapsed(true);
    return () => setSidebarCollapsed(previous);
  }, [setSidebarCollapsed]);

  /**
   * Hydration runs ONCE per questionnaire id, not on every change to `data`.
   *
   * It used to depend on `data`, which was safe only while saving was manual. With autosave every
   * save puts a fresh detail in the cache, so a `data`-keyed effect would re-hydrate from the server
   * about a second after each edit — discarding whatever was typed in the meantime and resetting
   * which card was open. Server ids are picked up from the save response instead (mergeServerIds).
   */
  const hydratedId = useRef<string | null>(null);
  useEffect(() => {
    if (!data || hydratedId.current === data.id) return;
    hydratedId.current = data.id;
    const next = fromDetail(data);
    setModel(next);
    setActiveSectionKey(next.sections[0]?.key ?? '');
  }, [data]);

  // ── Autosave ──────────────────────────────────────────────────────────────
  //
  // Debounced, and driven from an explicit `dirty` flag rather than by watching `model`: hydration
  // and the id-merge both write to `model`, and a model-watching effect would treat those as edits
  // and save in a loop.
  const dirty = useRef(false);
  const timer = useRef<number | undefined>(undefined);
  const savedTimer = useRef<number | undefined>(undefined);
  // Read inside the debounce callback so it always saves the LATEST model, not the one captured when
  // the timer was set.
  const latestModel = useRef(model);
  latestModel.current = model;

  const flush = useCallback(() => {
    if (!dirty.current || !questionnaireId) return;
    dirty.current = false;
    setSaveState('saving');

    save.mutate(toSaveInput(latestModel.current), {
      onSuccess: (saved) => {
        // Merge ids into the CURRENT model, which may have moved on while the request was in flight.
        setModel((current) => mergeServerIds(current, saved));
        setSaveState('saved');
        window.clearTimeout(savedTimer.current);
        savedTimer.current = window.setTimeout(() => setSaveState('idle'), SAVED_BADGE_MS);
      },
      onError: () => {
        // Marked dirty again so the next edit — or the unload guard — retries. Silently dropping a
        // failed autosave is how an admin loses an afternoon's work without ever being told.
        dirty.current = true;
        setSaveState('error');
      },
    });
  }, [questionnaireId, save]);

  /** Every mutator funnels through here, so nothing can change the model without scheduling a save. */
  const edit = useCallback(
    (update: (m: BuilderModel) => BuilderModel) => {
      setModel(update);
      dirty.current = true;
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(flush, AUTOSAVE_DELAY_MS);
    },
    [flush],
  );

  // Unmount: fire any pending save immediately. Leaving the builder inside the debounce window
  // would otherwise discard the last edit — the most likely one to be lost, since finishing an edit
  // and leaving are consecutive actions.
  useEffect(
    () => () => {
      window.clearTimeout(timer.current);
      window.clearTimeout(savedTimer.current);
      flush();
    },
    [flush],
  );

  // Closing the tab with an unsaved edit. The browser shows its own generic prompt; the message is
  // ignored by every current browser, which is why none is written here.
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (dirty.current) e.preventDefault();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  // ── Model mutators ────────────────────────────────────────────────────────

  const patchSection = (key: string, fn: (s: BuilderSection) => BuilderSection) =>
    edit((m) => ({ ...m, sections: m.sections.map((s) => (s.key === key ? fn(s) : s)) }));

  function addSection() {
    const section = newSection(model.sections.length + 1);
    edit((m) => ({ ...m, sections: [...m.sections, section] }));
    setActiveSectionKey(section.key);
  }

  function removeSection(key: string) {
    edit((m) => {
      const remaining = m.sections.filter((s) => s.key !== key);
      const sections = remaining.length > 0 ? remaining : [newSection(1)];
      // Selection moves from `sections` — the list actually being stored. Reading it from the stale
      // `model` outside meant deleting the last tab could select a key that no longer existed.
      if (activeSectionKey === key) setActiveSectionKey(sections[0]?.key ?? '');
      return { ...m, sections };
    });
  }

  function updateSection(key: string, patch: Partial<BuilderSection>) {
    patchSection(key, (s) => ({ ...s, ...patch }));
  }

  function addElement(fieldType: string) {
    const targetKey = activeSectionKey || model.sections[0]?.key;
    if (!targetKey) return;
    patchSection(targetKey, (s) => ({ ...s, fields: [...s.fields, newField(fieldType)] }));
  }

  function updateField(sectionKey: string, fieldKey: string, patch: Partial<BuilderField>) {
    patchSection(sectionKey, (s) => ({
      ...s,
      fields: s.fields.map((f) => (f.key === fieldKey ? { ...f, ...patch } : f)),
    }));
  }

  function removeField(sectionKey: string, fieldKey: string) {
    patchSection(sectionKey, (s) => ({ ...s, fields: s.fields.filter((f) => f.key !== fieldKey) }));
  }

  function duplicateField(sectionKey: string, fieldKey: string) {
    patchSection(sectionKey, (s) => {
      const idx = s.fields.findIndex((f) => f.key === fieldKey);
      const source = s.fields[idx];
      if (idx < 0 || !source) return s;
      // A fresh key and a null serverId — the copy must be a NEW row, or the save would move the
      // original instead of adding to it. `config` is deep-cloned for the same reason the registry's
      // defaults are: a shared reference would let editing one copy's options change the other's.
      const copy: BuilderField = {
        ...source,
        key: crypto.randomUUID(),
        serverId: null,
        config: structuredClone(source.config),
      };
      const fields = [...s.fields];
      fields.splice(idx + 1, 0, copy);
      return { ...s, fields };
    });
  }

  // ── Header actions ────────────────────────────────────────────────────────

  /**
   * Publish — always publish, never toggle.
   *
   * Meaningful even when the questionnaire is already live, because edits autosave: this flushes the
   * pending save and republishes the current state. Unpublishing is done from the status pill on the
   * list, alongside every other status change.
   */
  function handlePublish() {
    if (!questionnaireId) return;

    // Flushed FIRST. Publishing an unsaved questionnaire would make the last edits live by omission —
    // the status changes on the server while those edits still sit only in this tab.
    flush();

    changeStatus.mutate(
      { questionnaireId, status: 'Active' },
      {
        onSuccess: () => toast.success('Questionnaire published'),
        onError: () => toast.error('Failed to publish questionnaire'),
      },
    );
  }

  function handleDelete() {
    if (!questionnaireId) return;
    deleteQuestionnaire.mutate(questionnaireId, {
      onSuccess: () => {
        // Cleared so the unmount flush does not try to save a questionnaire that no longer exists.
        dirty.current = false;
        toast.success('Questionnaire deleted');
        navigate('/questionnaires');
      },
      onError: () => {
        toast.error('Failed to delete questionnaire');
        setConfirmingDelete(false);
      },
    });
  }

  return (
    <AppShell title="Questionnaires" hideTopbar noPadding>
      <div className="flex h-dvh flex-col overflow-hidden text-black-logo">
        <QuestionnaireBuilderHeader
          questionnaireId={questionnaireId}
          name={model.name}
          description={model.description}
          saveState={saveState}
          onNameChange={(name) => edit((m) => ({ ...m, name }))}
          onDescriptionChange={(description) => edit((m) => ({ ...m, description }))}
          onPublish={handlePublish}
          onDelete={() => setConfirmingDelete(true)}
          isPublishing={changeStatus.isPending}
        />

        {isLoading ? (
          <div className="flex flex-1 items-center justify-center gap-2 bg-[#F4F6FD] text-[length:var(--text-14)] text-gray66">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading questionnaire…
          </div>
        ) : (
          /* No padding and no gap: the designer's three columns sit FLUSH against each other and
             against the header, separated only by their own borders. Ours had a 16px grey gutter
             around and between them, which is what made each panel look like a floating card. */
          // The designer's own grid, transcribed from `.sc-questionnaires-builder`:
          //
          //   clamp(260px, 22vw, 352px)  |  minmax(320px, 840px)  |  minmax(280px, 1fr)
          //
          // The middle column is CAPPED AT 840px and the PREVIEW takes the remainder. Ours had the
          // middle on `flex-1` with no cap and the preview on a fixed clamp, so on a wide screen every
          // spare pixel went to the config panel — which is why it read as too wide and the phone
          // looked cramped. Reversing which column absorbs the slack is the whole fix.
          <div className="flex min-h-0 flex-1 overflow-hidden bg-[#F4F6FD] lg:grid lg:grid-cols-[clamp(260px,22vw,352px)_minmax(320px,840px)_minmax(280px,1fr)] lg:grid-rows-[minmax(0,1fr)]">
            <div className="hidden min-w-0 lg:flex">
              <QuestionnairesSidebar onAddElement={addElement} />
            </div>
            <div className="flex min-w-0 flex-1 lg:flex-none">
              <QuestionnairesConfigPanel
                sections={model.sections}
                activeSectionKey={activeSectionKey}
                onSelectSection={setActiveSectionKey}
                onAddSection={addSection}
                onRemoveSection={removeSection}
                onUpdateSection={updateSection}
                onUpdateField={updateField}
                onRemoveField={removeField}
                onDuplicateField={duplicateField}
              />
            </div>
            <div className="hidden min-w-0 lg:flex">
              <QuestionnairesMobilePreview
                sections={model.sections}
                activeSectionKey={activeSectionKey}
                onSelectSection={setActiveSectionKey}
              />
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirmingDelete}
        title={`Delete "${model.name}"?`}
        description="This permanently deletes the questionnaire and all its sections and fields. This cannot be undone."
        confirmLabel="Delete"
        destructive
        isPending={deleteQuestionnaire.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirmingDelete(false)}
      />
    </AppShell>
  );
}
